/**
 * Support deflection analytics — proves the AI agent's ROI and points at
 * intents that need tuning: bot-resolved %, response times, escalation
 * reasons, low-confidence intents, and volume patterns.
 */
import { useMemo, useState } from 'react';
import { PageHeader } from '@/components/layout/PageHeader';
import { BarChart } from '@/components/charts/BarChart';
import { KPICard } from '@/components/charts/KPICard';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { ErrorState } from '@/components/ui/ErrorState';
import { useConversationAnalytics } from '@/api/bi/crmConversations';
import { CHANNEL_META } from '@/components/crm/inbox/channelMeta';
import { cn } from '@/lib/cn';

const RANGES = [7, 30, 90] as const;

export function CrmConversationAnalyticsPage() {
  const [days, setDays] = useState<number>(30);
  const q = useConversationAnalytics(days);
  const data = q.data;

  const dailyTotals = useMemo(() => {
    if (!data) return [];
    const byDay = new Map<string, number>();
    for (const row of data.daily_volume) {
      byDay.set(row.day, (byDay.get(row.day) ?? 0) + row.conversations);
    }
    return [...byDay.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([day, conversations]) => ({
        day: day.slice(5), // MM-DD
        conversations,
      }));
  }, [data]);

  const hourly = useMemo(() => {
    if (!data) return [];
    const byHour = new Map<number, number>(data.hourly_volume.map((h) => [h.hour, h.messages]));
    return Array.from({ length: 24 }, (_, h) => ({
      hour: `${String(h).padStart(2, '0')}h`,
      messages: byHour.get(h) ?? 0,
    }));
  }, [data]);

  if (q.isError) {
    return <ErrorState title="Failed to load analytics" onRetry={() => q.refetch()} />;
  }

  return (
    <>
      <PageHeader
        eyebrow="CRM"
        title="Support analytics"
        description="How much customer support the AI handles on its own, how fast conversations get answered, and where humans still step in."
        actions={
          <div className="flex gap-1 rounded-lg border border-border p-0.5">
            {RANGES.map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setDays(r)}
                className={cn(
                  'rounded-md px-2.5 py-1 text-xs font-medium',
                  days === r ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted',
                )}
              >
                {r}d
              </button>
            ))}
          </div>
        }
      />

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <KPICard
          label="Conversations"
          value={data?.overview.total_conversations ?? '—'}
          loading={q.isLoading}
          hint={`Customer conversations opened in the last ${days} days across all channels.`}
        />
        <KPICard
          label="Bot-resolved"
          value={data ? `${data.overview.bot_resolved_pct}%` : '—'}
          loading={q.isLoading}
          hint="Closed conversations where the AI replied and no human agent ever had to send a message. This is your deflection rate."
        />
        <KPICard
          label="Agent-handled"
          value={data?.overview.agent_handled ?? '—'}
          loading={q.isLoading}
          hint="Conversations where a human agent sent at least one reply."
        />
        <KPICard
          label="Escalated"
          value={data?.overview.escalated ?? '—'}
          loading={q.isLoading}
          hint="Conversations the AI escalated to the team (ticket created)."
        />
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <KPICard
          label="Chat carts created"
          value={data?.ordering_funnel.carts_created ?? '—'}
          loading={q.isLoading}
          hint="Carts the AI built from conversations after the customer confirmed the items."
        />
        <KPICard
          label="Carts converted"
          value={data?.ordering_funnel.carts_converted ?? '—'}
          loading={q.isLoading}
          hint="Chat-created carts that became real orders in the app."
        />
        <KPICard
          label="Cart conversion"
          value={data ? `${data.ordering_funnel.conversion_pct}%` : '—'}
          loading={q.isLoading}
          hint="Share of chat-created carts that were checked out."
        />
        <KPICard
          label="Cart nudges sent"
          value={data?.ordering_funnel.nudges_sent ?? '—'}
          loading={q.isLoading}
          hint="One-time reminders sent for carts that sat unordered."
        />
      </div>

      <div className="mt-4 grid gap-4 xl:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Daily conversation volume</CardTitle>
          </CardHeader>
          <CardBody>
            {q.isLoading ? <Skeleton className="h-56 w-full" /> : <BarChart data={dailyTotals} xKey="day" yKey="conversations" height={230} />}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Messages by hour of day (NPT)</CardTitle>
          </CardHeader>
          <CardBody>
            {q.isLoading ? <Skeleton className="h-56 w-full" /> : <BarChart data={hourly} xKey="hour" yKey="messages" height={230} />}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Per-channel breakdown</CardTitle>
          </CardHeader>
          <CardBody className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-2 font-medium">Channel</th>
                  <th className="px-4 py-2 text-right font-medium">Conversations</th>
                  <th className="px-4 py-2 text-right font-medium">Closed</th>
                  <th className="px-4 py-2 text-right font-medium">Escalated</th>
                  <th className="px-4 py-2 text-right font-medium">Median first response</th>
                </tr>
              </thead>
              <tbody>
                {(data?.per_channel ?? []).map((row) => {
                  const rt = data?.response_times.find((r) => r.channel === row.channel);
                  const median = rt?.median_ai_seconds ?? rt?.median_agent_seconds ?? null;
                  return (
                    <tr key={row.channel} className="border-b border-border/50 last:border-0">
                      <td className="px-4 py-2">{CHANNEL_META[row.channel]?.label ?? row.channel}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{row.conversations}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{row.closed}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{row.escalated}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{formatSeconds(median)}</td>
                    </tr>
                  );
                })}
                {!q.isLoading && (data?.per_channel ?? []).length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-6 text-center text-xs text-muted-foreground">
                      No conversations in this period.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Escalation reasons</CardTitle>
            <p className="mt-1 text-xs text-muted-foreground">
              Intents that ended with a human ticket — the best candidates for new FAQ answers or prompt tuning.
            </p>
          </CardHeader>
          <CardBody>
            {q.isLoading ? (
              <Skeleton className="h-56 w-full" />
            ) : (data?.escalation_reasons ?? []).length === 0 ? (
              <p className="py-8 text-center text-xs text-muted-foreground">No escalations in this period.</p>
            ) : (
              <BarChart
                data={(data?.escalation_reasons ?? []).slice(0, 8)}
                xKey="intent"
                yKey="count"
                horizontal
                height={Math.max(160, (data?.escalation_reasons.length ?? 0) * 34)}
              />
            )}
          </CardBody>
        </Card>

        <Card className="xl:col-span-2">
          <CardHeader>
            <CardTitle>Low-confidence intents (drafted, not auto-sent)</CardTitle>
            <p className="mt-1 text-xs text-muted-foreground">
              Where the AI wrote a reply but wasn't confident enough to send it. High draft counts with decent
              average confidence are candidates for adding to the auto-send list; low confidence means the FAQ
              corpus needs better coverage.
            </p>
          </CardHeader>
          <CardBody className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-2 font-medium">Intent</th>
                  <th className="px-4 py-2 text-right font-medium">Drafts</th>
                  <th className="px-4 py-2 text-right font-medium">Avg confidence</th>
                </tr>
              </thead>
              <tbody>
                {(data?.low_confidence_intents ?? []).map((row) => (
                  <tr key={row.intent} className="border-b border-border/50 last:border-0">
                    <td className="px-4 py-2 font-mono text-xs">{row.intent}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{row.drafts}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{Math.round(row.avg_confidence * 100)}%</td>
                  </tr>
                ))}
                {!q.isLoading && (data?.low_confidence_intents ?? []).length === 0 ? (
                  <tr>
                    <td colSpan={3} className="px-4 py-6 text-center text-xs text-muted-foreground">
                      Nothing drafted below the confidence threshold — the agent is either auto-sending or escalating.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </CardBody>
        </Card>
      </div>
    </>
  );
}

function formatSeconds(s: number | null): string {
  if (s == null) return '—';
  if (s < 60) return `${Math.round(s)}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  return `${(s / 3600).toFixed(1)}h`;
}
